import React, { useEffect, useState } from "react";
import {
  Megaphone,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowLeft,
  Gift,
  FileText,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Socio } from "../types/Socio.tsx";
import socioService from "../services/socioService.tsx";
import { useAuth0 } from "@auth0/auth0-react";
import {
  obtenerCampaniaPorId,
  listarCampaniasPorSocio,
  asociarSocioACampania,
  aceptarCampania,
  rechazarCampania,
  Campania,
  SocioCampania,
} from "../services/campaniaService.ts";

export default function CampaniaDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [socioData, setSocioData] = useState<Socio | null>(null);
  const [campania, setCampania] = useState<Campania | null>(null);
  const [miAsociacion, setMiAsociacion] = useState<SocioCampania | null>(null);
  const [loading, setLoading] = useState(true);
  const [accionando, setAccionando] = useState(false);
  const [msg, setMsg] = useState<{ tipo: "ok" | "error"; texto: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      const stored = localStorage.getItem("socioSeleccionado");
      if (!stored) return;
      try {
        const parsed = JSON.parse(stored) as Socio;
        setSocioData(parsed);
      } catch {}
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const campaniaData = await obtenerCampaniaPorId(Number(id), token);
        setCampania(campaniaData);

        if (socioData?.socio_id) {
          const asociaciones = await listarCampaniasPorSocio(Number(socioData.socio_id), token);
          const mi = asociaciones.find((a) => a.campania_id === Number(id));
          setMiAsociacion(mi || null);
        }
      } catch (e) {
        console.error("Error al cargar campaña:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, socioData, getAccessTokenSilently]);

  const handleUnirse = async () => {
    if (!campania || !socioData) return;
    setAccionando(true);
    setMsg(null);
    try {
      const token = await getAccessTokenSilently();
      const result = await asociarSocioACampania(
        { socioId: Number(socioData.socio_id), campaniaId: campania.campania_id },
        token
      );
      setMiAsociacion(result);
      setMsg({ tipo: "ok", texto: "Te uniste a la campaña. Ahora podés aceptarla." });
    } catch (e: any) {
      const texto = e?.response?.data?.message || "Error al unirse a la campaña";
      setMsg({ tipo: "error", texto });
    } finally {
      setAccionando(false);
    }
  };

  const handleAceptar = async () => {
    if (!miAsociacion) return;
    setAccionando(true);
    setMsg(null);
    try {
      const token = await getAccessTokenSilently();
      await aceptarCampania(miAsociacion.socio_campania_id, token);
      setMiAsociacion({ ...miAsociacion, estado: "aceptada", aceptado_en: new Date().toISOString() });
      setMsg({ tipo: "ok", texto: "Campaña aceptada correctamente" });
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al aceptar la campaña" });
    } finally {
      setAccionando(false);
    }
  };

  const handleRechazar = async () => {
    if (!miAsociacion) return;
    setAccionando(true);
    setMsg(null);
    try {
      const token = await getAccessTokenSilently();
      await rechazarCampania(miAsociacion.socio_campania_id, token);
      setMiAsociacion({ ...miAsociacion, estado: "rechazada" });
      setMsg({ tipo: "ok", texto: "Campaña rechazada" });
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al rechazar la campaña" });
    } finally {
      setAccionando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!campania) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <AlertCircle className="h-12 w-12 text-slate-400" />
        <p className="mt-4 text-slate-600">Campaña no encontrada</p>
        <button onClick={() => navigate("/campanias")} className="mt-4 text-blue-600 hover:underline">
          Volver a campañas
        </button>
      </div>
    );
  }

  const yaAceptada = miAsociacion?.estado === "aceptada";
  const estaPendiente = miAsociacion?.estado === "pendiente";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate("/campanias")}
          className="mb-6 flex items-center gap-1 text-sm font-medium text-slate-600 transition hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Volver a campañas
        </button>

        {msg && (
          <div
            className={`mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${
              msg.tipo === "ok"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-rose-50 text-rose-700 border border-rose-200"
            }`}
          >
            {msg.tipo === "ok" ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            {msg.texto}
          </div>
        )}

        {/* Hero Banner */}
        <div className="mb-8 overflow-hidden rounded-3xl bg-white shadow-xl">
          {campania.imagen_url && (
            <div className="h-56 overflow-hidden sm:h-72">
              <img src={campania.imagen_url} alt={campania.nombre} className="h-full w-full object-cover" />
            </div>
          )}
          {!campania.imagen_url && (
            <div className="flex h-56 items-center justify-center bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 sm:h-72">
              <Megaphone className="h-20 w-20 text-white/60" />
            </div>
          )}

          <div className="p-6 sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex-1">
                <div className="mb-3 flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{campania.nombre}</h1>
                  {campania.descuento && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-3 py-1 text-sm font-bold text-indigo-700">
                      <Gift className="h-4 w-4" /> {campania.descuento}
                    </span>
                  )}
                </div>
                {campania.descripcion && (
                  <p className="text-slate-600 leading-relaxed">{campania.descripcion}</p>
                )}
              </div>
              {campania.logo_url && (
                <div className="flex-shrink-0">
                  <img src={campania.logo_url} alt="Logo" className="h-16 w-16 rounded-xl object-contain" />
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4" />
                {new Date(campania.fecha_inicio).toLocaleDateString("es-AR")} - {new Date(campania.fecha_fin).toLocaleDateString("es-AR")}
              </span>
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                campania.estado === "activa"
                  ? "bg-emerald-100 text-emerald-700"
                  : campania.estado === "borrador"
                  ? "bg-amber-100 text-amber-700"
                  : "bg-slate-100 text-slate-600"
              }`}>
                {campania.estado}
              </span>
            </div>

            {/* Acciones */}
            <div className="mt-8">
              {!socioData ? (
                <p className="text-sm text-slate-500">Seleccioná un socio para participar de la campaña.</p>
              ) : !miAsociacion ? (
                <button
                  disabled={accionando}
                  onClick={handleUnirse}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700 disabled:opacity-50"
                >
                  {accionando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
                  Unirme a esta campaña
                </button>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  {yaAceptada ? (
                    <span className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-700 border border-emerald-200">
                      <CheckCircle2 className="h-5 w-5" /> Participás de esta campaña
                    </span>
                  ) : estaPendiente ? (
                    <>
                      <span className="inline-flex items-center gap-1.5 rounded-xl bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-700 border border-amber-200">
                        <Clock className="h-4 w-4" /> Pendiente de aceptación
                      </span>
                      <button
                        disabled={accionando}
                        onClick={handleAceptar}
                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {accionando ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        Aceptar campaña
                      </button>
                      <button
                        disabled={accionando}
                        onClick={handleRechazar}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                      >
                        <XCircle className="h-4 w-4" /> Rechazar
                      </button>
                    </>
                  ) : miAsociacion.estado === "rechazada" ? (
                    <span className="inline-flex items-center gap-2 rounded-xl bg-rose-50 px-5 py-3 text-sm font-semibold text-rose-700 border border-rose-200">
                      <XCircle className="h-5 w-5" /> Rechazaste esta campaña
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Términos y condiciones */}
        {campania.terminos && (
          <div className="rounded-2xl border border-slate-100 bg-white/70 p-6 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-400" />
              <h2 className="text-lg font-semibold text-slate-900">Términos y Condiciones</h2>
            </div>
            <div className="prose prose-sm max-w-none text-slate-600 whitespace-pre-wrap">{campania.terminos}</div>
          </div>
        )}
      </div>
    </div>
  );
}
